import React, { useContext } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { Toaster } from 'components/ui/sonner';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { LeagueProvider, LeagueContext } from './context/LeagueContext';
import AdminLayout from './components/layout/AdminLayout';
import Login from './components/Login';
import DraftRankings from './components/mitchRoom/DraftRankings';
import Lineups from './components/mitchRoom/Lineups';
import StatsLabels from './components/mitchRoom/StatsLabels';
import ModellingArea from './components/mitchRoom/ModellingArea';
import ModelOutputs from './components/mitchRoom/ModelOutputs';
import { RevenueAnalytics } from './components/mitchRoom/revenueAnalytics';
import CreatorsAdmin from './components/creators/CreatorsAdmin';
import { WebAnalytics } from './components/mitchRoom/webAnalytics';
import { UserAnalytics } from './components/mitchRoom/userAnalytics';
import BlogEditor from './components/mitchRoom/BlogEditor';
import BlogPostEditor from './components/mitchRoom/BlogPostEditor';
import SocialShare from './components/mitchRoom/SocialShare';
import BlogGeneratorPage from './components/blog/generator/BlogGeneratorPage';
import VideoPicker from './components/blog/generator/VideoPicker';
import PromptsPage from './components/blog/prompts/PromptsPage';
import UserManagement from './components/settings/UserManagement';
import BrandingManager from './components/settings/BrandingManager';
import PlatformSettings from './components/settings/PlatformSettings';

// Protected route wrapper that requires authentication
function ProtectedRoute({ children }) {
  const { user } = useContext(AuthContext);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
}

// Rankings page wrapper
function RankingsPage() {
  const { user } = useContext(AuthContext);
  return <DraftRankings user={user} setLoading={() => {}} setError={() => {}} />;
}

// Lineups page wrapper
function LineupsPage() {
  const { user } = useContext(AuthContext);
  const { activeLeague } = useContext(LeagueContext);
  return (
    <Lineups
      user={user}
      activeLeague={activeLeague}
      setLoading={() => {}}
      setError={() => {}}
    />
  );
}

// /blog/generate → picker; /blog/generate?video=<id> → generator page for a new post
function GenerateEntry() {
  const [params] = useSearchParams();
  return params.get('video') ? <BlogGeneratorPage /> : <VideoPicker />;
}

function App() {
  return (
    <AuthProvider>
      <LeagueProvider>
        <Routes>
          {/* Login route */}
          <Route path="/login" element={<Login />} />

          {/* Protected admin routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <RankingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rankings"
            element={
              <ProtectedRoute>
                <RankingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lineups"
            element={
              <ProtectedRoute>
                <LineupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/labels"
            element={
              <ProtectedRoute>
                <StatsLabels />
              </ProtectedRoute>
            }
          />
          <Route
            path="/modelling"
            element={
              <ProtectedRoute>
                <ModellingArea />
              </ProtectedRoute>
            }
          />
          <Route
            path="/model-outputs"
            element={
              <ProtectedRoute>
                <ModelOutputs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/revenue"
            element={
              <ProtectedRoute>
                <RevenueAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creators"
            element={
              <ProtectedRoute>
                <CreatorsAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/web-analytics"
            element={
              <ProtectedRoute>
                <WebAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-analytics"
            element={
              <ProtectedRoute>
                <UserAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog"
            element={
              <ProtectedRoute>
                <BlogEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog/new"
            element={
              <ProtectedRoute>
                <BlogPostEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog/edit/:id"
            element={
              <ProtectedRoute>
                <BlogPostEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog/generate"
            element={
              <ProtectedRoute>
                <GenerateEntry />
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog/generate/:postId"
            element={
              <ProtectedRoute>
                <BlogGeneratorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog/prompts"
            element={
              <ProtectedRoute>
                <PromptsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/social-share"
            element={
              <ProtectedRoute>
                <SocialShare />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/branding"
            element={
              <ProtectedRoute>
                <BrandingManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/platform-settings"
            element={
              <ProtectedRoute>
                <PlatformSettings />
              </ProtectedRoute>
            }
          />

          {/* Catch all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster />
      </LeagueProvider>
    </AuthProvider>
  );
}

export default App;
